import Color from '../helpers/color/Color';
import StringArt from '../infra/StringArt';
import Circle from '../shapes/Circle';
import { ControlsConfig } from '../types/config.types';
import {
  ColorConfig,
  ColorMap,
  ColorValue,
} from '../helpers/color/color.types';
import { CalcOptions } from '../types/stringart.types';
import { withoutAttribute } from '../helpers/config_utils';
import Controller from '../infra/Controller';
import Nails from '../infra/nails/Nails';

const COLOR_CONFIG = Color.getConfig({
  defaults: {
    isMultiColor: true,
    colorCount: 7,
    color: '#ffbb29',
    multicolorRange: 21,
    multicolorStart: 32,
    multicolorByLightness: true,
    minLightness: 36,
    maxLightness: 98,
  },
});

interface SpiralConfig extends ColorConfig {
  n: number;
  repetition: number;
  innerLength: number;
  rotation: number;
  distortion: number;
}

type TCalc = {
  circle: Circle;
  realRepetition: number;
};

export default class Spiral extends StringArt<SpiralConfig, TCalc> {
  static type = 'spiral';

  id = 'spiral';
  name = 'Spiral';
  controls: ControlsConfig<SpiralConfig> = [
    // @ts-ignore
    {
      ...Circle.nailsConfig,
      defaultValue: 200,
    },
    {
      key: 'repetition',
      label: 'Repetition',
      defaultValue: 5,
      type: 'range',
      attr: { min: 1, max: 20, step: 1 },
      affectsNails: false,
      isStructural: true,
    },
    {
      key: 'innerLength',
      label: 'Spiral thickness',
      defaultValue: 0.5,
      type: 'range',
      attr: {
        min: ({ n }) => 1 / n,
        max: 1,
        step: ({ n }) => 1 / n,
      },
      displayValue: ({ n, innerLength }) => Math.round(n * innerLength),
      affectsNails: false,
    },
    {
      ...withoutAttribute(Circle.rotationConfig, 'snap'),
      defaultValue: 0.75,
    },
    Circle.distortionConfig,
    COLOR_CONFIG,
  ];

  private colorMap: ColorMap;

  getCalc({ size }: CalcOptions): TCalc {
    const { n, rotation, margin, repetition, distortion } = this.config;

    const circleConfig = {
      size,
      n,
      margin,
      rotation,
      distortion,
    };

    return {
      circle: new Circle(circleConfig),
      realRepetition: repetition * 2 - 1,
    };
  }

  setUpDraw() {
    if (this.color.config.colorCount) {
      this.colorMap = this.color.getColorMap({
        stepCount: this.getStepCount(),
        colorCount: this.color.config.colorCount,
      });
    }
  }

  getAspectRatio(options: CalcOptions): number {
    const { circle } = this.getCalc(options);
    return circle.getAspectRatio();
  }

  *drawSpiral(
    controller: Controller,
    {
      shift = 0,
      color = '#ffffff',
    }: { shift?: number; color?: ColorValue } = {}
  ): Generator<void> {
    const { innerLength, n } = this.config;

    let currentInnerLength = Math.round(innerLength * n);
    let repetitionCount = 0;
    controller.startLayer({ color });
    controller.goto(shift);

    let prevPointIndex = shift;
    let isPrevPoint = false;

    for (let i = 0; currentInnerLength > 0; i++) {
      if (this.colorMap) {
        if (this.colorMap.has(i)) {
          controller.startLayer({ color: this.colorMap.get(i) });
        }
      }

      prevPointIndex = isPrevPoint
        ? prevPointIndex - currentInnerLength + 1
        : prevPointIndex + currentInnerLength;

      if (repetitionCount === this.calc.realRepetition) {
        currentInnerLength--;
        repetitionCount = 0;
        prevPointIndex++;
      } else {
        repetitionCount++;
      }

      controller.stringTo(prevPointIndex % n);

      yield;
      isPrevPoint = !isPrevPoint;
    }
  }

  *drawStrings(controller: Controller): Generator<void> {
    yield* this.drawSpiral(controller, {
      color: this.color.getColor(0),
    });
  }

  getStepCount(): number {
    const { innerLength, repetition, n } = this.config;
    return Math.round(n * (innerLength * 2) * repetition);
  }

  getNails(precision?: number): Nails {
    return this.calc.circle.getNails(precision);
  }

  thumbnailConfig = ({ n }) => ({
    n: Math.min(n, 60),
  });
}
